import React, { Fragment } from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Main-flow orientation stepper (Crea tu lista → Cotiza tu compra → Optimiza
 * tu compra). FIXED per-screen model: each screen represents one step by
 * itself — the list detail page always renders step 1 (create/confirm), the
 * CartSheet always renders step 2 (quote), step 3 is reserved for the optimize
 * screen. The step is NEVER derived from memory state.
 *
 * Purely presentational: conveys the current step and the next action on both
 * desktop (numbered circles + connectors) and mobile ("Paso X de 3 - label" +
 * "sig:" next chip + segmented progress bar). It MUST NOT navigate or mutate
 * state — no handlers anywhere.
 *
 * By default both variants render in the DOM; Tailwind `lg:` classes toggle
 * visibility (mirrors the page's desktop/mobile CTA pattern), so screen readers
 * see exactly one variant per viewport. With `forceCompact` the compact variant
 * renders UNCONDITIONALLY and the desktop <ol> is not rendered at all — used
 * inside the CartSheet, whose narrow 384px panel would clip the overflowing
 * desktop stepper.
 */

const STEPS = ["Crea tu lista", "Cotiza tu compra", "Optimiza tu compra"] as const;

/** Next-step chip label per current step, rendered as "sig: <label>"; step 3 (reserved) has no chip. */
const NEXT_CHIP: Record<1 | 2 | 3, string | undefined> = {
  1: "Cotizar",
  2: "Optimizar",
  3: undefined,
};

export interface MainFlowStepsProps {
  /** 1=crear, 2=cotizar, 3=optimizar (reserved, never current on this page). */
  currentStep: 1 | 2 | 3;
  /** Quote in-flight: renders the current step in-progress (pulse). */
  isExecuting?: boolean;
  /**
   * When true, renders ONLY the compact variant — the desktop <ol> is dropped
   * from the DOM and the compact block loses its `lg:hidden` toggle, so it is
   * visible on every viewport. Intended for narrow containers (CartSheet).
   * Default false: both variants render with their `lg:` visibility toggles.
   */
  forceCompact?: boolean;
}

export const MainFlowSteps: React.FC<MainFlowStepsProps> = ({
  currentStep,
  isExecuting = false,
  forceCompact = false,
}) => {
  return (
    <div data-testid="main-flow-steps" className="px-6 lg:px-8">
      {/* Desktop stepper (≥1024px): numbered circles + connectors. Skipped
          entirely when forceCompact (narrow surfaces like the CartSheet). */}
      {!forceCompact && (
        <ol aria-label="Progreso del flujo principal" className="hidden lg:flex items-center w-full">
          {STEPS.map((label, index) => {
            const step = (index + 1) as 1 | 2 | 3;
            const isCompleted = step < currentStep;
            const isActive = step === currentStep;
            return (
              <Fragment key={label}>
                <li
                  aria-current={isActive ? "step" : undefined}
                  className="flex items-center gap-3"
                >
                  {isCompleted ? (
                    <span
                      data-testid={`step-circle-${step}`}
                      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary"
                    >
                      <Check className="h-4 w-4" aria-hidden="true" />
                    </span>
                  ) : (
                    <span
                      data-testid={`step-circle-${step}`}
                      className={cn(
                        "flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-sm font-semibold",
                        isActive
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border bg-background text-muted-foreground",
                        isActive && isExecuting && "animate-pulse",
                      )}
                    >
                      {step}
                    </span>
                  )}
                  <span
                    className={cn(
                      "whitespace-nowrap text-sm",
                      isActive ? "font-semibold text-foreground" : "text-muted-foreground",
                    )}
                  >
                    {label}
                  </span>
                </li>
                {index < STEPS.length - 1 && (
                  <span
                    aria-hidden="true"
                    className={cn("h-0.5 flex-1", step < currentStep ? "bg-primary" : "bg-border")}
                  />
                )}
              </Fragment>
            );
          })}
        </ol>
      )}

      {/* Mobile compact variant: "Paso X de 3 - label" + "sig:" next chip
          + segmented progress bar (segments 1..currentStep filled, rest muted).
          When forceCompact the block renders on every viewport (no lg:hidden);
          otherwise it is hidden from lg up. */}
      <div className={forceCompact ? undefined : "lg:hidden"}>
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm font-medium text-foreground">
            Paso {currentStep} de 3 - {STEPS[currentStep - 1]}
          </p>
          {NEXT_CHIP[currentStep] ? (
            <span className="pointer-events-none select-none rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
              sig: {NEXT_CHIP[currentStep]}
            </span>
          ) : null}
        </div>
        <div
          aria-hidden="true"
          data-testid="mobile-progress-segments"
          className="mt-2 flex gap-1"
        >
          {[1, 2, 3].map((segment) => (
            <span
              key={segment}
              data-testid={`mobile-progress-segment-${segment}`}
              className={cn(
                "h-1 flex-1 rounded-full",
                segment <= currentStep ? "bg-primary" : "bg-border",
              )}
            />
          ))}
        </div>
      </div>
    </div>
  );
};